import { CustomInput } from '@/components/molecules/CustomInput';
import { useState } from 'react';
import { CustomButton } from '@/components/molecules/CustomButton';
import { loginByDiscord, loginByPassword } from '@/services/auth.ts';
import { useNavigate, useSearchParams } from 'react-router';
import { getMeUser } from '@/services/user.ts';
import { useToast } from '@/hooks/useToastContext.tsx';

export const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [loginType, setLoginType] = useState<'default' | 'discord'>('default');
  const [isLoading, setIsLoading] = useState(false);
  const { showToast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') ?? '/';

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await login();
  };

  const login = async () => {
    try {
      const me = await getMeUser();
      if (me) {
        navigate(redirectTo);
        return;
      }
    } catch (error) {
      console.log('Utilisateur non connecté ou erreur getMeUser:', error);
    }

    if (!username) {
      showToast('warning', "L'identifiant est requis.");
      return;
    }
    if (loginType === 'default' && !password) {
      showToast('warning', 'Le mot de passe est requis.');
      return;
    }

    setIsLoading(true);
    setPasswordError('');

    if (loginType === 'discord') {
      try {
        await loginByDiscord({ username: username.toLowerCase() });
        showToast('success', 'Authentification réussie !');
        navigate(redirectTo);
      } catch (error) {
        console.log(error);
        showToast('error', 'Authentification échouée.');
      }
    } else {
      try {
        await loginByPassword({ username, password });
        showToast('success', 'Authentification réussie !');
        navigate(redirectTo);
      } catch (error) {
        const message = error instanceof Error ? error.message : '';
        if (message.includes("pas de mot de passe")) {
          showToast('error', 'Ce compte utilise uniquement la connexion Discord.');
        } else if (message.toLowerCase().includes('mot de passe') || message.toLowerCase().includes('incorrect')) {
          setPasswordError('Mot de passe incorrect.');
        } else if (message.toLowerCase().includes('introuvable') || message.toLowerCase().includes('not found')) {
          showToast('error', 'Utilisateur introuvable.');
        } else {
          showToast('error', message || 'Authentification échouée.');
        }
      }
    }

    setIsLoading(false);
  };

  return (
    <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-[linear-gradient(147deg,rgba(57,87,106,1)_4%,rgba(239,186,43,1)_48%,rgba(109,20,23,1)_100%)] px-4 py-8">
      <div className="bg-surface w-full max-w-md rounded-2xl p-6 shadow-2xl sm:p-8">
        <h1 className="text-content mb-6 text-center text-2xl font-bold sm:text-3xl">
          Se connecter
        </h1>

        <p className="text-content mb-4 text-left text-sm italic">
          {loginType === 'discord'
            ? 'Se connecter avec Discord'
            : 'Se connecter avec votre Bitume-Connect'}
        </p>

        <form onSubmit={handleSubmit} className="flex flex-col gap-4">
          <CustomInput
            placeholder="identifiant"
            value={username}
            onChange={(newValue) => setUsername(newValue)}
          />

          {loginType === 'default' && (
            <div className="flex flex-col gap-1">
              <CustomInput
                placeholder="password"
                type="password"
                value={password}
                onChange={(newValue) => {
                  setPassword(newValue);
                  setPasswordError('');
                }}
                fullWidth
              />
              {passwordError && (
                <p className="text-red-500 text-xs">{passwordError}</p>
              )}
            </div>
          )}

          <a
            href="#"
            className="text-content self-end text-sm italic hover:underline"
          >
            Mot de passe oublié ?
          </a>

          <CustomButton
            type="submit"
            title="Se connecter"
            color="primary"
            loading={isLoading}
          />
        </form>

        <div className="my-6 flex items-center gap-4">
          <div className="h-px flex-1 bg-line-soft" />
          <span className="text-content text-sm">
            ou se connecter avec
          </span>
          <div className="h-px flex-1 bg-line-soft" />
        </div>

        <div className="flex flex-col gap-4">
          {loginType === 'discord' ? (
            <CustomButton
              onClick={() => setLoginType('default')}
              title="Se connecter avec Bitume-Connect"
              color="background"
            />
          ) : (
            <CustomButton
              onClick={() => setLoginType('discord')}
              title="Se connecter avec Discord"
              color="discord"
            />
          )}
        </div>
      </div>
    </div>
  );
};

